import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useToggle } from '../../../generic/hooks/useToggle'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { alpha } from '@mui/material/styles'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import useMosInfo from '../common/provider/useMosInfo'
import WorkerRequiredButton from '../common/WorkerRequiredButton'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import PropTypes from 'prop-types'
import { apiGet, apiPost, apiPut } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import { useQualityIncidenceDialogData } from './hooks/useQualityIncidenceDialogData'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import QualityIncidenceCreateForm from './components/QualityIncidenceCreateForm'
import QualityIncidenceHistoryView from './components/QualityIncidenceHistoryView'
import QualityIncidenceForm from '../../../quality/QualityIncidenceForm'
import useUser from '../../../generic/hooks/useUser/useUser'
import Document from '../../../configuration/document/Document'
import { QUALITY_INCIDENCE_PRIORITY_OPTIONS } from './qualityIncidencePriority'

const QUALITY_INCIDENCE_CACHE_TTL_MS = 30_000

/**
 * Debounce para valores controlados (p.ej. input de búsqueda) para evitar filtros/re-renders por cada tecla.
 * @template T
 * @param {T} value
 * @param {number} delayMs
 * @returns {T}
 */
const useDebouncedValue = (value, delayMs) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timerId = window.setTimeout(() => setDebouncedValue(value), Math.max(0, Number(delayMs) || 0))
    return () => window.clearTimeout(timerId)
  }, [delayMs, value])

  return debouncedValue
}

const PREVIEW_DATA = {
  active: { id: '00000000-0000-0000-0000-000000000001', name: 'INC-0001 · Superficie' },
  counts: { low: 2, medium: 1, high: 0, critical: 1 }
}

const DEFAULT_WIDGET_DATA = {
  active: null,
  counts: { low: 0, medium: 0, high: 0, critical: 0 }
}

const SeverityCard = ({ label, value, color }) => {
  return (
    <Paper
      variant='outlined'
      sx={{
        borderRadius: 1.5,
        borderColor: alpha(color, 0.55),
        backgroundColor: alpha('#000', 0.22),
        px: 1.5,
        py: 1.1,
        minHeight: 56,
        display: 'flex',
        alignItems: 'center'
      }}
      aria-label={`${label}: ${value}`}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
        <Typography
          variant='overline'
          sx={{
            color,
            fontWeight: 800,
            letterSpacing: 1.2,
            lineHeight: 1
          }}
        >
          {label}
        </Typography>
        <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1 }}>
          {value}
        </Typography>
      </Stack>
    </Paper>
  )
}

const getSeverityOptions = (t_, palette) => ([
  { key: 'low', label: t_('Low'), color: palette.success.main },
  { key: 'medium', label: t_('Medium'), color: palette.warning.main },
  { key: 'high', label: t_('High'), color: palette.warning.dark },
  { key: 'critical', label: t_('Critical'), color: palette.error.main }
])

const getWorkerLabel = (worker) => {
  if (!worker) return ''
  if (typeof worker === 'string') return worker
  const code = String(worker.code || '').trim()
  const name = String(worker.name || '').trim()
  const label = [code, name].filter(Boolean).join(' - ')
  if (label) return label
  if (worker.name) return String(worker.name)
  if (worker.fullName) return String(worker.fullName)
  if (worker.username) return String(worker.username)
  if (worker.code) return String(worker.code)
  if (worker.id) return String(worker.id)
  return ''
}

const normalizeJobOption = (item) => {
  const id = item?.uuid ?? item?.id ?? null
  const name = String(item?.name ?? item?.code ?? '').trim()
  if (!id || !name) return null
  return { id, name, raw: item }
}

// Default incidence to show when the widget opens for a job: keep the current selection if still present,
// otherwise prefer an incidence that has actions (that is why the actions icon was clicked), falling back
// to the first/most recent one. Without this, a job with several incidences would always open the most
// recent, even when an older one is the one holding the pending actions.
const pickDefaultIncidenceUuid = (incidences, previousUuid) => {
  if (previousUuid && incidences.some((item) => item?.uuid === previousUuid)) {
    return previousUuid
  }
  const incidenceWithActions = incidences.find((item) => item?.hasAction)
  return (incidenceWithActions || incidences[0])?.uuid ?? null
}

const toFabricationOrderOption = (item) => ({
  id: item?.id ?? null,
  code: String(item?.fabricationOrder ?? item?.code ?? item?.name ?? '').trim(),
  raw: item
})

const toDeviceOption = (item) => ({
  id: item?.id ?? null,
  name: String(item?.name ?? item?.code ?? '').trim(),
  raw: item
})

const STATUS_OPTIONS = [
  { value: 'draft', labelKey: 'Draft' },
  { value: 'pending', labelKey: 'Pending' }
]

const normalizeStatusOptions = (response) => {
  return normalizeApiList(response)
    .map((status) => {
      const value = String(status?.value ?? status?.code ?? status?.name ?? status ?? '').trim().toUpperCase()
      if (!value || value === 'DRAFT') return null
      return {
        value,
        labelKey: value
      }
    })
    .filter(Boolean)
}

const PRIORITY_OPTIONS = QUALITY_INCIDENCE_PRIORITY_OPTIONS

const DEFAULT_INCIDENCE_FORM_STATE = {
  title: '',
  code: '',
  description: '',
  status: 'draft',
  priority: 'LOW',
  createdDate: new Date(),
  endDate: null,
  preliminaryAnalysis: '',
  proposedSolution: '',
  jobLabel: '',
  scopeRows: [],
  scopesEndDate: null,
  incidenceActions: [],
  responsibleName: ''
}

// Builds a scope row preloaded with the FO + main job from which the incidence is being created,
// so that scope is mandatory and the operator does not have to re-select what is already known.
const buildPrechargedScopeRows = (fabricationOrder, node) => {
  const fabricationOrderId = fabricationOrder?.id ?? null
  if (fabricationOrderId == null) return []
  const fabricationOrderCode = String(
    fabricationOrder?.code ?? fabricationOrder?.fabricationOrder ?? fabricationOrder?.name ?? ''
  ).trim()
  const jobId = node?.uuid ?? node?.id ?? null
  const jobName = String(node?.name ?? '').trim()
  return [{
    scopeRowId: `precharged-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    fabricationOrderId,
    fabricationOrderCode,
    jobs: jobId != null || jobName ? [{ id: jobId, name: jobName }] : [],
    resource: [],
    affected: ''
  }]
}

const mapQualityIncidenceStatus = (status) => {
  const normalized = String(status || '').trim().toUpperCase()
  if (normalized === 'DRAFT') return 'DRAFT'
  if (normalized) return normalized
  return 'PENDING'
}

const mapQualityIncidencePriority = (priority) => {
  const normalized = String(priority || '').trim().toUpperCase()
  if (normalized === 'MEDIUM') return 'MEDIUM'
  if (normalized === 'HIGH') return 'HIGH'
  if (normalized === 'CRITICAL') return 'CRITICAL'
  return 'LOW'
}

const parseLongId = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return null
  const parsed = Number.parseInt(normalized, 10)
  if (Number.isNaN(parsed)) return null
  return parsed
}

const normalizeEntityId = (value) => {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized.toLowerCase() === 'null' || normalized.toLowerCase() === 'undefined') return null
  const parsed = Number.parseInt(normalized, 10)
  return String(parsed) === normalized ? parsed : normalized
}

const toIdOnlyDto = (value) => {
  const id = normalizeEntityId(value?.id ?? value)
  if (id == null) return null
  return { id }
}

const toNullableIdOnlyDto = (value) => {
  const id = normalizeEntityId(value?.id ?? value)
  if (id == null) return null
  return { id }
}

const toFabricationOrderDto = (fabricationOrder, fallbackId) => {
  const id = normalizeEntityId(fabricationOrder?.id ?? fallbackId)
  if (id == null) return null
  return {
    id
  }
}

const parseAffectedUnits = (value, fallbackValue = null) => {
  const normalized = String(value || '').trim()
  if (!normalized) return fallbackValue
  const parsed = Number.parseInt(normalized, 10)
  if (Number.isNaN(parsed) || parsed < 1) return fallbackValue
  return parsed
}

const getAffectedUnits = (row, fallbackValue = 1) => (
  parseAffectedUnits(row?.affectedUnits ?? row?.affected, fallbackValue)
)

const sumAffectedUnits = (rows) => {
  const affectedRows = Array.isArray(rows) ? rows : []
  if (affectedRows.length === 0) return ''
  return String(affectedRows.reduce((total, row) => total + getAffectedUnits(row, 1), 0))
}

const getAffectedResource = (row) => (
  row?.resource || row?.rawMaterial || row?.material || row?.qualityIncidenceAffectedRawMaterial || row?.qualityIncidenceAffectedRawMaterialDto || null
)

const getRawMaterialReference = (row) => {
  const resource = getAffectedResource(row)
  return row?.rawMaterialReference ||
    row?.rawMaterialReferenceDto ||
    row?.qualityIncidenceAffectedRawMaterialReference ||
    row?.qualityIncidenceAffectedRawMaterialReferenceDto ||
    resource?.rawMaterialReference ||
    resource?.rawMaterialReferenceDto ||
    null
}

const getRawMaterialReferenceId = (row) => {
  const resource = getAffectedResource(row)
  const reference = getRawMaterialReference(row)
  return row?.rawMaterialReferenceId ??
    row?.rawMaterialReferenceDtoId ??
    reference?.id ??
    reference?.resourceId ??
    resource?.rawMaterialReferenceId ??
    resource?.rawMaterialReferenceDtoId ??
    null
}

const getAffectedResourceId = (row) => {
  const resource = getAffectedResource(row)
  return getRawMaterialReferenceId(row) ??
    row?.resourceId ??
    resource?.resourceId ??
    resource?.id ??
    null
}

const getAffectedResourceDisplay = (row) => {
  const resource = getAffectedResource(row)
  const reference = getRawMaterialReference(row)
  return {
    code: String(row?.rawMaterialReferenceCode ?? row?.resourceReferenceCode ?? reference?.resourceCode ?? reference?.code ?? row?.resourceCode ?? resource?.resourceCode ?? resource?.code ?? '').trim(),
    name: String(row?.rawMaterialReferenceName ?? row?.rawMaterialReferenceDescription ?? row?.resourceReferenceName ?? reference?.resourceName ?? reference?.name ?? reference?.description ?? row?.resourceName ?? resource?.resourceName ?? resource?.name ?? '').trim()
  }
}

const buildQualityIncidenceScopeAffectedList = ({ resources, affected, endDate = null }) => {
  const rows = Array.isArray(resources) ? resources : []
  return rows
    .map((row) => {
      const resourceId = normalizeEntityId(row?.rawMaterialReferenceId ?? row?.rawMaterialReferenceDtoId ?? row?.resourceId ?? row?.id)
      if (resourceId == null) return null
      return {
        uuid: null,
        type: 'RAW_MATERIAL',
        resourceId,
        endDate,
        affectedUnits: parseAffectedUnits(row?.affected ?? affected, 1)
      }
    })
    .filter(Boolean)
}

// Each UI scope row may target one or many jobs. The backend stores one node per scope, so we
// explode the row into N scope DTOs sharing the FO. Each resource carries the jobId it belongs
// to (per the per-job materials picker), so we route it to the matching scope split — instead
// of the legacy "first scope owns everything" rule. Untagged resources fall back to the first
// scope so legacy data without jobId still saves.
const buildQualityIncidenceScopes = ({ scopeRows, scopesEndDate }) => {
  const rows = Array.isArray(scopeRows) ? scopeRows : []
  return rows.flatMap((row) => {
    const fabricationOrder = toFabricationOrderDto(null, row?.fabricationOrderId)
    if (!fabricationOrder) return []
    const allResources = Array.isArray(row?.resource) ? row.resource : []
    const jobs = Array.isArray(row?.jobs) && row.jobs.length > 0
      ? row.jobs
      : (row?.job ? [row.job] : [null])
    const jobIdSet = new Set(jobs.map((job) => String(job?.id)).filter((v) => v !== 'null' && v !== ''))
    return jobs.map((job, index) => {
      const matchingResources = allResources.filter((resource) => {
        if (resource?.jobId != null && String(resource.jobId) === String(job?.id)) return true
        if (index === 0 && (resource?.jobId == null || !jobIdSet.has(String(resource.jobId)))) return true
        return false
      })
      return {
        uuid: job?.scopeUuid || (index === 0 ? (row?.uuid || null) : null),
        fabricationOrder,
        endDate: scopesEndDate || null,
        node: toNullableIdOnlyDto(job?.id ?? null),
        affected: buildQualityIncidenceScopeAffectedList({
          resources: matchingResources,
          affected: row?.affected,
          endDate: scopesEndDate || null
        })
      }
    })
  })
}

const buildQualityIncidenceDtoFromForm = ({ incidenceForm, fabricationOrder, node, device, worker, responsibleName }) => {
  const currentStatus = String(incidenceForm.status || '').trim()
  const createdDate = incidenceForm.createdDate instanceof Date ? incidenceForm.createdDate : new Date()
  const endDate = incidenceForm.endDate instanceof Date ? incidenceForm.endDate : null

  return {
    uuid: null,
    title: String(incidenceForm.title || '').trim(),
    code: String(incidenceForm.code || '').trim(),
    description: String(incidenceForm.description || '').trim(),
    status: mapQualityIncidenceStatus(currentStatus),
    fabricationOrder: toFabricationOrderDto(fabricationOrder, null),
    preliminaryAnalysis: String(incidenceForm.preliminaryAnalysis || '').trim() || null,
    proposedSolution: String(incidenceForm.proposedSolution || '').trim() || null,
    node: toNullableIdOnlyDto(incidenceForm.jobLabel ? node?.id : null),
    createdDate,
    endDate,
    device: toIdOnlyDto(device?.id),
    priority: mapQualityIncidencePriority(incidenceForm.priority),
    worker: toIdOnlyDto(worker?.id),
    responsibleName: String(responsibleName || '').trim() || null,
    affected: [],
    scopes: buildQualityIncidenceScopes({
      scopeRows: incidenceForm.scopeRows,
      scopesEndDate: incidenceForm.scopesEndDate
    }),
    incidenceActions: Array.isArray(incidenceForm.incidenceActions)
      ? incidenceForm.incidenceActions.map((action) => ({
        uuid: null,
        actionType: action.actionType,
        procedureText: action.procedureText,
        recipientsText: action.recipientsText,
        mailDate: action.mailDate
      }))
      : []
  }
}

const getQualityIncidenceCodeFromResponse = (response) => {
  const primitiveCode = String(response ?? '').trim()
  if (primitiveCode && primitiveCode !== '[object Object]') return primitiveCode

  const directCode = String(response?.code || '').trim()
  if (directCode) return directCode

  const nestedCode = String(response?.data?.code || response?.qualityIncidence?.code || '').trim()
  if (nestedCode) return nestedCode

  return ''
}

const getQualityIncidenceUuidFromResponse = (response) => {
  const directUuid = String(response?.uuid || '').trim()
  if (directUuid) return directUuid

  const nestedUuid = String(response?.data?.uuid || response?.qualityIncidence?.uuid || response?.qualityIncidenceDto?.uuid || '').trim()
  if (nestedUuid) return nestedUuid

  return ''
}

const normalizeApiList = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.content)) return response.content
  return []
}

const getEntityDisplayName = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  return String(value?.name || value?.fullName || value?.code || value?.fabricationOrder || value?.title || value?.id || '').trim()
}

const normalizeQualityIncidenceScopeRows = (scopes) => {
  const rows = Array.isArray(scopes) ? scopes : []
  const groupsByKey = new Map()

  rows.forEach((row, index) => {
    const fabricationOrderCode = getEntityDisplayName(row?.fabricationOrder)
    const fabricationOrderId = row?.fabricationOrder?.id ?? null
    const groupKey = String(fabricationOrderId ?? fabricationOrderCode ?? `scope-${index}`)
    const scopeAffected = Array.isArray(row?.affected) ? row.affected : []
    const jobName = getEntityDisplayName(row?.node)

    if (!groupsByKey.has(groupKey)) {
      groupsByKey.set(groupKey, {
        scopeRowId: String(row?.uuid || `${groupKey}-${index}`),
        fabricationOrderCode,
        jobNames: [],
        resources: [],
        resourceKeys: new Set(),
        endDate: row?.endDate || null
      })
    }
    const group = groupsByKey.get(groupKey)

    if (jobName && !group.jobNames.includes(jobName)) {
      group.jobNames.push(jobName)
    }

    scopeAffected.forEach((item, affectedIndex) => {
      const resourceId = getAffectedResourceId(item)
      if (resourceId == null) return
      // Each scope-affected DB row is its own entry. Dedup by the affected uuid (or a fallback
      // composite key) so several instances of the same raw material reference inside one job
      // are NOT collapsed into a single chip.
      const dedupKey = item?.uuid
        ? `uuid::${item.uuid}`
        : `${jobName || ''}::${resourceId}::${affectedIndex}`
      if (group.resourceKeys.has(dedupKey)) return
      group.resourceKeys.add(dedupKey)
      const { code, name } = getAffectedResourceDisplay(item)
      group.resources.push({
        resourceId,
        jobName,
        resourceCode: code,
        resourceName: name,
        affected: String(getAffectedUnits(item, 1))
      })
    })

    if (!group.endDate && row?.endDate) group.endDate = row.endDate
  })

  return Array.from(groupsByKey.values())
    .map(({ resourceKeys, ...group }) => ({
      ...group,
      jobName: group.jobNames.join(', '),
      affected: sumAffectedUnits(group.resources)
    }))
    .filter((row) => row.fabricationOrderCode || row.jobName || row.resources.length > 0)
}

const getActionTargetType = (row) => {
  if (row?.qualityIncidence) return 'qualityIncidence'
  if (row?.scopeDto || row?.qualityIncidenceScope) return 'qualityIncidenceScope'
  if (row?.affected) return 'qualityIncidenceAffected'
  if (row?.scopeAffected) return 'qualityIncidenceScopeAffected'
  return ''
}

const normalizeQualityIncidenceActionRows = (actions) => {
  const rows = Array.isArray(actions) ? actions : []
  return rows.map((row, index) => ({
    actionRowId: String(row?.uuid || `action-${index}`),
    uuid: row?.uuid,
    code: String(row?.code || '').trim(),
    description: String(row?.description || '').trim(),
    preliminaryAnalysis: String(row?.preliminaryAnalysis || '').trim(),
    actionType: String(row?.actionType || 'CORRECTIVE').toUpperCase(),
    actionStatus: String(row?.actionStatus || 'PENDING').toUpperCase(),
    actionResult: String(row?.actionResult || 'PENDING').toUpperCase(),
    comment: String(row?.comment || '').trim(),
    executionComment: String(row?.executionComment || '').trim(),
    expirationDate: row?.expirationDate || null,
    executionDate: row?.executionDate || null,
    createDate: row?.createDate || null,
    workerName: getEntityDisplayName(row?.worker),
    responsible: getEntityDisplayName(row?.responsible),
    resourceName: getEntityDisplayName(row?.resource),
    procedureText: String(row?.procedureText || row?.description || '').trim(),
    recipientsText: String(row?.recipientsText || '').trim(),
    mailDate: row?.mailDate || row?.createDate || null,
    actionParentUuid: row?.actionParent?.uuid || null,
    targetType: getActionTargetType(row)
  }))
}

export const QualityIncidenceWidget = ({
  data,
  createDialogOpen = null,
  onCreateDialogClose,
  onSaved,
  manualContext = false,
  hideTrigger = false
}) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const { user } = useUser()
  const { format, formatDate } = useDateFormatter()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const mosInfo = useMosInfo() || {}
  const {
    worker,
    pendingQualityIncidenceOpen,
    consumeQualityIncidenceOpen,
    qualityIncidencePayload,
    clearQualityIncidencePayload
  } = mosInfo
  const fabricationOrder = qualityIncidencePayload?.fabricationOrder ?? mosInfo.fabricationOrder
  const node = qualityIncidencePayload?.node ?? mosInfo.node
  const jobReference = qualityIncidencePayload?.jobReference ?? mosInfo.jobReference
  const device = qualityIncidencePayload?.device ?? mosInfo.device
  const isJobActive = Boolean(fabricationOrder?.id && node?.id)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [manualFabricationOrders, setManualFabricationOrders] = useState([])
  const [manualJobOptions, setManualJobOptions] = useState([])
  const [manualDeviceOptions, setManualDeviceOptions] = useState([])
  const [selectedManualFabricationOrder, setSelectedManualFabricationOrder] = useState(null)
  const [selectedManualNode, setSelectedManualNode] = useState(null)
  const [selectedManualDevice, setSelectedManualDevice] = useState(null)
  const [selectedManualWorker, setSelectedManualWorker] = useState(null)
  const [isLoadingManualFabricationOrders, setIsLoadingManualFabricationOrders] = useState(false)
  const [isLoadingManualDevices, setIsLoadingManualDevices] = useState(false)
  const [manualStatusOptions, setManualStatusOptions] = useState([])
  const [incidenceForm, setIncidenceForm] = useState({ ...DEFAULT_INCIDENCE_FORM_STATE })
  const [hasTriedSubmitIncidenceForm, setHasTriedSubmitIncidenceForm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [AlertElement, showAlert] = AlertUI()
  const [openIncidences, setOpenIncidences] = useState([])
  const [isLoadingOpenIncidences, setIsLoadingOpenIncidences] = useState(false)
  const [isLoadingOpenIncidencesError, setIsLoadingOpenIncidencesError] = useState(false)
  const [selectedOpenIncidenceUuid, setSelectedOpenIncidenceUuid] = useState(null)
  const [selectedOpenIncidenceDetails, setSelectedOpenIncidenceDetails] = useState(null)
  const [isLoadingSelectedOpenIncidenceDetails, setIsLoadingSelectedOpenIncidenceDetails] = useState(false)
  const [isLoadingSelectedOpenIncidenceDetailsError, setIsLoadingSelectedOpenIncidenceDetailsError] = useState(false)
  const [selectedOpenIncidenceActions, setSelectedOpenIncidenceActions] = useState([])
  const [isLoadingSelectedOpenIncidenceActions, setIsLoadingSelectedOpenIncidenceActions] = useState(false)
  const [isLoadingSelectedOpenIncidenceActionsError, setIsLoadingSelectedOpenIncidenceActionsError] = useState(false)
  const [historyPanel, setHistoryPanel] = useState('list')
  const [detailsTab, setDetailsTab] = useState('overview')
  const [incidenceSearchQuery, setIncidenceSearchQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('ALL')
  const [openIncidencesReloadNonce, setOpenIncidencesReloadNonce] = useState(0)
  const [selectedOpenIncidenceDetailsReloadNonce, setSelectedOpenIncidenceDetailsReloadNonce] = useState(0)
  const [actionsReloadNonce, setActionsReloadNonce] = useState(0)
  const [summaryReloadNonce, setSummaryReloadNonce] = useState(0)
  const [summaryData, setSummaryData] = useState(null)
  const [isLoadingSummary, setIsLoadingSummary] = useState(false)
  const [showForm, setShowForm] = useToggle()
  const formRef = useRef()
  const [currentUuid, setCurrentUuid] = useState(null)
  const [openDocument, setOpenDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const debouncedIncidenceSearchQuery = useDebouncedValue(incidenceSearchQuery, 160)
  const openIncidencesCacheRef = useRef({ ts: 0, incidences: null })
  // Guards the "jump to the Actions tab on open" so it happens only once per job context (not on reloads
  // or after the user manually changes tab).
  const initialDetailsTabAppliedRef = useRef(false)
  const incidenceDetailsCacheRef = useRef(new Map())
  const previousCreateDialogOpenRef = useRef(createDialogOpen)

  const creationFabricationOrder = manualContext ? selectedManualFabricationOrder : fabricationOrder
  const creationNode = manualContext ? selectedManualNode : node
  const creationDevice = manualContext ? selectedManualDevice : device
  const creationWorker = manualContext ? selectedManualWorker : worker
  const fabricationOrderDisplayName = String(creationFabricationOrder?.name || creationFabricationOrder?.fabricationOrder || creationFabricationOrder?.code || '').trim()
  const jobDisplayName = String(creationNode?.name || (!manualContext ? jobReference?.name : '') || '').trim()
  const deviceDisplayName = String(creationDevice?.name || '').trim()
  const workerDisplayName = getWorkerLabel(creationWorker)

  useEffect(() => {
    if (createDialogOpen === null) return
    const wasOpen = previousCreateDialogOpenRef.current
    previousCreateDialogOpenRef.current = createDialogOpen
    setIsDialogOpen(Boolean(createDialogOpen))
    setIsCreateFormOpen(Boolean(createDialogOpen))
    if (createDialogOpen && !wasOpen) {
      setHistoryPanel('list')
      setHasTriedSubmitIncidenceForm(false)
      setSelectedManualFabricationOrder(null)
      setSelectedManualNode(null)
      setSelectedManualDevice(null)
      setSelectedManualWorker(null)
      setManualJobOptions([])
      setCurrentUuid(null)
      setIncidenceForm({
        ...DEFAULT_INCIDENCE_FORM_STATE,
        status: manualContext ? 'PENDING' : DEFAULT_INCIDENCE_FORM_STATE.status,
        responsibleName: user.name,
        jobLabel: manualContext ? '' : jobDisplayName,
        createdDate: new Date(),
        scopeRows: manualContext ? [] : buildPrechargedScopeRows(fabricationOrder, node)
      })
    }
  }, [createDialogOpen, manualContext, user.name, jobDisplayName, fabricationOrder, node])

  useEffect(() => {
    if (!manualContext || !isDialogOpen) return

    let cancelled = false
    const loadManualContextOptions = async () => {
      setIsLoadingManualFabricationOrders(true)
      setIsLoadingManualDevices(true)
      try {
        const [fabricationOrdersResponse, statusResponse, devicesResponse] = await Promise.all([
          apiGet('allFabricationOrderSimple', { all: false }),
          apiGet('getQualityIncidenceStatus'),
          apiGet('deviceIdCodeName')
        ])
        if (cancelled) return
        setManualFabricationOrders(normalizeApiList(fabricationOrdersResponse)
          .map(toFabricationOrderOption)
          .filter((item) => item.id != null && item.code))
        setManualDeviceOptions(normalizeApiList(devicesResponse)
          .map(toDeviceOption)
          .filter((item) => item.id != null && item.name))
        setManualStatusOptions(normalizeStatusOptions(statusResponse))
      } catch (_) {
        if (cancelled) return
        setManualFabricationOrders([])
        setManualDeviceOptions([])
        setManualStatusOptions([])
      } finally {
        if (!cancelled) {
          setIsLoadingManualFabricationOrders(false)
          setIsLoadingManualDevices(false)
        }
      }
    }

    loadManualContextOptions()

    return () => {
      cancelled = true
    }
  }, [isDialogOpen, manualContext])

  useEffect(() => {
    if (!manualContext) return
    const fabricationOrderId = selectedManualFabricationOrder?.id
    if (!fabricationOrderId) {
      setManualJobOptions([])
      return
    }

    let cancelled = false
    const loadJobs = async () => {
      try {
        const workflow = await apiGet(`fabricationOrderJobs/${fabricationOrderId}`)
        if (cancelled) return
        const sourceItems = Array.isArray(workflow?.nodes) ? workflow.nodes : normalizeApiList(workflow)
        setManualJobOptions(sourceItems
          .filter((item) => Array.isArray(workflow?.nodes)
            ? String(item?.nodeType ?? '').toUpperCase() === 'JOB' || item?.jobReference
            : true)
          .map(normalizeJobOption)
          .filter(Boolean))
      } catch (_) {
        if (cancelled) return
        setManualJobOptions([])
      }
    }

    loadJobs()

    return () => {
      cancelled = true
    }
  }, [manualContext, selectedManualFabricationOrder?.id])

  useEffect(() => {
    if (manualContext) return
    setSummaryData(null)
    setOpenIncidences([])
    setSelectedOpenIncidenceUuid(null)
    setSelectedOpenIncidenceDetails(null)
    setSelectedOpenIncidenceActions([])
    setCurrentUuid(null)
    setOpenDocument(false)
    setIsLoadingSelectedOpenIncidenceActions(false)
    setIsLoadingSelectedOpenIncidenceActionsError(false)
    setIncidenceSearchQuery('')
    setPriorityFilter('ALL')
    setIsCreateFormOpen(false)
    setHistoryPanel('list')
    setDetailsTab('overview')
    initialDetailsTabAppliedRef.current = false
    setHasTriedSubmitIncidenceForm(false)
    setIncidenceForm({
      ...DEFAULT_INCIDENCE_FORM_STATE,
      jobLabel: jobDisplayName,
      createdDate: new Date(),
      scopeRows: buildPrechargedScopeRows(fabricationOrder, node)
    })
    openIncidencesCacheRef.current = { ts: 0, incidences: null }
    incidenceDetailsCacheRef.current.clear()
  }, [fabricationOrder, jobDisplayName, manualContext, node])

  const dialogData = useQualityIncidenceDialogData({
    isOpen: isDialogOpen && isCreateFormOpen,
    fabricationOrder: creationFabricationOrder,
    node: creationNode,
    selectedJobLabel: incidenceForm.jobLabel
  })
  const widgetData = data || DEFAULT_WIDGET_DATA

  const activeIncident = summaryData?.qualityIncidenceSimpleDto || widgetData.active

  const severityCounts = useMemo(() => {
    if (summaryData?.qualityIncidencePriorityCountDto) {
      const counts = summaryData.qualityIncidencePriorityCountDto
      return {
        low: counts.lowCount || 0,
        medium: counts.mediumCount || 0,
        high: counts.highCount || 0,
        critical: counts.criticalCount || 0
      }
    }
    return widgetData.counts
  }, [summaryData, widgetData.counts])

  const severityOptions = useMemo(() => {
    return getSeverityOptions(t_, theme.palette)
  }, [t_, theme.palette])

  useEffect(() => {
    if (!fabricationOrder?.id) return

    let cancelled = false
    const loadSummary = async (isAutoRefresh = false) => {
      if (!isAutoRefresh) setIsLoadingSummary(true)
      try {
        const fabricationOrderId = fabricationOrder?.id ?? null
        const nodeUUID = node?.uuid ?? node?.id ?? null

        if (!fabricationOrderId || !nodeUUID) {
          return
        }

        const response = await apiGet(`lastQualityIncidenceAndPriorityTypesCount/${fabricationOrderId}/${nodeUUID}`)
        if (cancelled) return
        setSummaryData(response)
      } catch (error) {
        console.error('Error fetching quality incidence summary', error)
      } finally {
        if (!cancelled && !isAutoRefresh) setIsLoadingSummary(false)
      }
    }

    loadSummary()

    const intervalId = setInterval(() => {
      loadSummary(true)
    }, 4000)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [fabricationOrder?.id, node?.uuid, node?.id, summaryReloadNonce])

  const handleRefreshSummary = useCallback(() => {
    setSummaryReloadNonce((v) => v + 1)
  }, [])

  const statusSelectOptions = useMemo(() => {
    const options = manualContext ? manualStatusOptions : STATUS_OPTIONS
    const fallbackOptions = manualContext && manualStatusOptions.length === 0
      ? [{ value: 'PENDING', labelKey: 'PENDING' }]
      : options
    return fallbackOptions.map((option) => ({ value: option.value, label: t_(option.labelKey) }))
  }, [manualContext, manualStatusOptions, t_])

  const statusValueByLabel = useMemo(() => {
    const map = new Map()
    statusSelectOptions.forEach((opt) => map.set(opt.label, opt.value))
    return map
  }, [statusSelectOptions])

  const statusLabelByValue = useMemo(() => {
    const map = new Map()
    statusSelectOptions.forEach((opt) => map.set(opt.value, opt.label))
    return map
  }, [statusSelectOptions])

  const prioritySelectOptions = useMemo(() => {
    return PRIORITY_OPTIONS.map((option) => ({ value: option.value, label: t_(option.labelKey) }))
  }, [t_])

  const priorityValueByLabel = useMemo(() => {
    const map = new Map()
    prioritySelectOptions.forEach((opt) => map.set(opt.label, opt.value))
    return map
  }, [prioritySelectOptions])

  const priorityLabelByValue = useMemo(() => {
    const map = new Map()
    prioritySelectOptions.forEach((opt) => map.set(opt.value, opt.label))
    return map
  }, [prioritySelectOptions])

  useEffect(() => {
    if (!isDialogOpen || isCreateFormOpen) return

    let cancelled = false

    // On the first load for this job context, open straight on the Actions tab when the default incidence
    // has actions (i.e. the actions icon was clicked). Runs once per open, so it never fights the user.
    const applyInitialActionsTab = (incidences) => {
      if (initialDetailsTabAppliedRef.current) return
      initialDetailsTabAppliedRef.current = true
      const defaultIncidence = incidences.find((item) => item?.uuid === pickDefaultIncidenceUuid(incidences, null))
      if (defaultIncidence?.hasAction) {
        setDetailsTab('actions')
      }
    }

    const loadOpenIncidences = async () => {
      const now = Date.now()
      const cached = openIncidencesCacheRef.current
      const hasFreshCache = Array.isArray(cached.incidences) && now - cached.ts < QUALITY_INCIDENCE_CACHE_TTL_MS

      if (hasFreshCache) {
        setOpenIncidences(cached.incidences)
        setSelectedOpenIncidenceUuid((prev) => pickDefaultIncidenceUuid(cached.incidences, prev))
        applyInitialActionsTab(cached.incidences)
        setIsLoadingOpenIncidences(false)
        setIsLoadingOpenIncidencesError(false)
        if (openIncidencesReloadNonce === 0) return
      }

      setIsLoadingOpenIncidences(true)
      setIsLoadingOpenIncidencesError(false)
      try {
        const fabricationOrderId = fabricationOrder?.id || 'null'
        const nodeUUID = node?.uuid || node?.id || 'null'
        const response = await apiGet(`qualityIncidence/${fabricationOrderId}/${nodeUUID}`)
        if (cancelled) return
        const nextIncidences = normalizeApiList(response)
        openIncidencesCacheRef.current = { ts: Date.now(), incidences: nextIncidences }
        setOpenIncidences(nextIncidences)
        setSelectedOpenIncidenceUuid((prev) => pickDefaultIncidenceUuid(nextIncidences, prev))
        applyInitialActionsTab(nextIncidences)
      } catch (_) {
        if (cancelled) return
        setOpenIncidences([])
        setSelectedOpenIncidenceUuid(null)
        setIsLoadingOpenIncidencesError(true)
      } finally {
        if (!cancelled) {
          setIsLoadingOpenIncidences(false)
        }
      }
    }

    loadOpenIncidences()

    return () => {
      cancelled = true
    }
  }, [
    isCreateFormOpen,
    isDialogOpen,
    openIncidencesReloadNonce,
    fabricationOrder?.id,
    node?.uuid,
    node?.id
  ])

  useEffect(() => {
    if (!isDialogOpen || isCreateFormOpen || !selectedOpenIncidenceUuid) {
      setSelectedOpenIncidenceDetails(null)
      setIsLoadingSelectedOpenIncidenceDetails(false)
      setIsLoadingSelectedOpenIncidenceDetailsError(false)
      return
    }

    let cancelled = false

    const loadSelectedOpenIncidenceDetails = async () => {
      const cached = incidenceDetailsCacheRef.current.get(selectedOpenIncidenceUuid) || null
      const now = Date.now()
      if (cached && now - cached.ts < QUALITY_INCIDENCE_CACHE_TTL_MS) {
        setSelectedOpenIncidenceDetails(cached.value || null)
        setIsLoadingSelectedOpenIncidenceDetails(false)
        setIsLoadingSelectedOpenIncidenceDetailsError(false)
        return
      }

      setIsLoadingSelectedOpenIncidenceDetails(true)
      setIsLoadingSelectedOpenIncidenceDetailsError(false)
      try {
        const response = await apiGet(`getQualityIncidenceByUuid/${selectedOpenIncidenceUuid}`)
        if (cancelled) return
        incidenceDetailsCacheRef.current.set(selectedOpenIncidenceUuid, { ts: Date.now(), value: response || null })
        setSelectedOpenIncidenceDetails(response || null)
      } catch (_) {
        if (cancelled) return
        setSelectedOpenIncidenceDetails(null)
        setIsLoadingSelectedOpenIncidenceDetailsError(true)
      } finally {
        if (!cancelled) {
          setIsLoadingSelectedOpenIncidenceDetails(false)
        }
      }
    }

    loadSelectedOpenIncidenceDetails()

    return () => {
      cancelled = true
    }
  }, [isCreateFormOpen, isDialogOpen, selectedOpenIncidenceUuid, selectedOpenIncidenceDetailsReloadNonce])

  useEffect(() => {
    const nodeUUID = node?.uuid || node?.id || null
    if (!isDialogOpen || isCreateFormOpen || !selectedOpenIncidenceUuid || !fabricationOrder?.id || !nodeUUID) {
      setSelectedOpenIncidenceActions([])
      setIsLoadingSelectedOpenIncidenceActions(false)
      setIsLoadingSelectedOpenIncidenceActionsError(false)
      return
    }

    let cancelled = false

    const loadSelectedOpenIncidenceActions = async () => {
      setIsLoadingSelectedOpenIncidenceActions(true)
      setIsLoadingSelectedOpenIncidenceActionsError(false)
      try {
        const fabricationOrderId = fabricationOrder.id
        const response = await apiGet(`getQualityIncidenceActions/${selectedOpenIncidenceUuid}/${fabricationOrderId}/${nodeUUID}`)
        if (cancelled) return
        setSelectedOpenIncidenceActions(normalizeApiList(response))
      } catch (error) {
        console.error('Error fetching quality incidence actions', error)
        if (cancelled) return
        setSelectedOpenIncidenceActions([])
        setIsLoadingSelectedOpenIncidenceActionsError(true)
      } finally {
        if (!cancelled) {
          setIsLoadingSelectedOpenIncidenceActions(false)
        }
      }
    }

    loadSelectedOpenIncidenceActions()

    return () => {
      cancelled = true
    }
  }, [isCreateFormOpen, isDialogOpen, selectedOpenIncidenceUuid, fabricationOrder?.id, node?.uuid, node?.id, actionsReloadNonce])

  const selectedOpenIncidence = useMemo(() => {
    return openIncidences.find((item) => item?.uuid === selectedOpenIncidenceUuid) || openIncidences[0] || null
  }, [openIncidences, selectedOpenIncidenceUuid])

  const selectedOpenIncidenceDisplay = selectedOpenIncidenceDetails || selectedOpenIncidence
  const selectedOpenIncidenceLabel = String(selectedOpenIncidenceDisplay?.title || selectedOpenIncidenceDisplay?.name || t_('No active incident')).trim()
  const selectedOpenIncidenceStatus = String(selectedOpenIncidenceDisplay?.status || 'OPEN').trim().toUpperCase()
  const selectedOpenIncidenceCode = String(selectedOpenIncidenceDisplay?.code || '').trim()
  const selectedOpenIncidencePriority = String(selectedOpenIncidenceDisplay?.priority || 'MEDIUM').trim().toUpperCase()
  const selectedOpenIncidenceCreatedDate = selectedOpenIncidenceDisplay?.createdDate || null
  const selectedOpenIncidenceEndDate = selectedOpenIncidenceDisplay?.endDate || null
  const selectedOpenIncidenceDescription = String(selectedOpenIncidenceDisplay?.description || '').trim()
  const selectedOpenIncidencePreliminaryAnalysis = String(selectedOpenIncidenceDisplay?.preliminaryAnalysis || '').trim()
  const selectedOpenIncidenceProposedSolution = String(selectedOpenIncidenceDisplay?.proposedSolution || selectedOpenIncidenceDisplay?.solutionProposed || '').trim()
  const selectedOpenIncidenceFabricationOrderName = getEntityDisplayName(selectedOpenIncidenceDisplay?.fabricationOrder) || fabricationOrderDisplayName
  const selectedOpenIncidenceJobName = getEntityDisplayName(selectedOpenIncidenceDisplay?.node) || jobDisplayName
  const selectedOpenIncidenceDeviceName = getEntityDisplayName(selectedOpenIncidenceDisplay?.device) || deviceDisplayName
  const selectedOpenIncidenceWorkerName = getEntityDisplayName(selectedOpenIncidenceDisplay?.worker) || workerDisplayName
  const selectedOpenIncidenceResponsibleName = getEntityDisplayName(selectedOpenIncidenceDisplay?.responsibleName)
  const selectedOpenIncidenceScopeRows = useMemo(() => normalizeQualityIncidenceScopeRows(selectedOpenIncidenceDisplay?.scopes), [selectedOpenIncidenceDisplay?.scopes])
  const selectedOpenIncidenceActionRows = useMemo(() => normalizeQualityIncidenceActionRows(selectedOpenIncidenceActions), [selectedOpenIncidenceActions])

  const resetForm = useCallback(() => {
    setHasTriedSubmitIncidenceForm(false)
    setIncidenceForm({
      ...DEFAULT_INCIDENCE_FORM_STATE,
      status: manualContext ? 'PENDING' : DEFAULT_INCIDENCE_FORM_STATE.status,
      responsibleName: user.name,
      jobLabel: manualContext ? '' : jobDisplayName,
      createdDate: new Date(),
      scopeRows: manualContext ? [] : buildPrechargedScopeRows(fabricationOrder, node)
    })
    setCurrentUuid(null)
    if (manualContext) {
      setSelectedManualFabricationOrder(null)
      setSelectedManualNode(null)
      setSelectedManualDevice(null)
      setSelectedManualWorker(null)
      setManualJobOptions([])
    }
  }, [jobDisplayName, manualContext, user.name, fabricationOrder, node])

  const openDialog = useCallback(() => {
    setIsDialogOpen(true)
    setIsCreateFormOpen(false)
    setHistoryPanel('list')
    setDetailsTab('overview')
    setIncidenceSearchQuery('')
    setPriorityFilter('ALL')
  }, [])

  useEffect(() => {
    if (!pendingQualityIncidenceOpen) return
    openDialog()
    if (consumeQualityIncidenceOpen) {
      consumeQualityIncidenceOpen()
    }
  }, [pendingQualityIncidenceOpen, openDialog, consumeQualityIncidenceOpen])

  const openCreateForm = useCallback(() => {
    resetForm()
    setIsCreateFormOpen(true)
  }, [resetForm])

  const openEditForm = useCallback(() => {
    if (selectedOpenIncidenceDisplay == null) return
    if (selectedOpenIncidenceStatus !== 'DRAFT') return
    setCurrentUuid(selectedOpenIncidenceDisplay?.uuid)
    setShowForm()
  }, [selectedOpenIncidenceDisplay, selectedOpenIncidenceStatus, setShowForm])

  const closeDialog = useCallback(() => {
    resetForm()
    setIsCreateFormOpen(false)
    setIsDialogOpen(false)
    setHistoryPanel('list')
    onCreateDialogClose?.()
    if (clearQualityIncidencePayload) {
      clearQualityIncidencePayload()
    }
  }, [resetForm, clearQualityIncidencePayload, onCreateDialogClose])

  const wasDialogOpenRef = useRef(false)
  useEffect(() => {
    if (wasDialogOpenRef.current && !isDialogOpen) {
      if (qualityIncidencePayload && clearQualityIncidencePayload) {
        clearQualityIncidencePayload()
      }
    }
    wasDialogOpenRef.current = isDialogOpen
  }, [isDialogOpen, qualityIncidencePayload, clearQualityIncidencePayload])

  useEffect(() => {
    if (!isDialogOpen || isCreateFormOpen) return
    if (!isSmallScreen) return
    if (!selectedOpenIncidenceUuid) return
    setHistoryPanel('details')
  }, [isCreateFormOpen, isDialogOpen, isSmallScreen, selectedOpenIncidenceUuid])

  const filteredOpenIncidences = useMemo(() => {
    const query = String(debouncedIncidenceSearchQuery || '').trim().toLowerCase()
    const priority = String(priorityFilter || 'ALL').trim().toUpperCase()
    const list = Array.isArray(openIncidences) ? openIncidences : []

    return list.filter((incidence) => {
      const incidenceStatus = String(incidence?.status || '').trim().toUpperCase()
      const incidencePriority = String(incidence?.priority || '').trim().toUpperCase()
      const incidenceLabel = String(incidence?.title || incidence?.name || '').trim()
      const incidenceCode = String(incidence?.code || '').trim()

      const matchesPriority = priority === 'ALL' || incidencePriority === priority
      if (!matchesPriority) return false

      if (!query) return true
      const haystack = [incidenceCode, incidenceLabel, incidenceStatus, incidencePriority].join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [debouncedIncidenceSearchQuery, openIncidences, priorityFilter])

  const clearListFilters = useCallback(() => {
    setIncidenceSearchQuery('')
    setPriorityFilter('ALL')
  }, [])

  const handleRefreshOpenIncidences = useCallback(() => {
    setOpenIncidencesReloadNonce((v) => v + 1)
  }, [])

  const handleRefreshSelectedOpenIncidenceDetails = useCallback((uuid = selectedOpenIncidenceUuid) => {
    if (uuid) incidenceDetailsCacheRef.current.delete(uuid)
    setSelectedOpenIncidenceDetails(null)
    setSelectedOpenIncidenceDetailsReloadNonce((v) => v + 1)
  }, [selectedOpenIncidenceUuid])

  const handleRefreshActions = useCallback(() => {
    setActionsReloadNonce((v) => v + 1)
  }, [])

  // Called after an action is executed (confirmed/rejected) from the execution dialog, which already
  // performed the API call. Refreshes the actions list, the summary and the incidences list since the
  // action status / incidence state may have changed.
  const handleActionExecuted = useCallback(() => {
    handleRefreshActions()
    handleRefreshSummary()
    handleRefreshOpenIncidences()
  }, [handleRefreshActions, handleRefreshSummary, handleRefreshOpenIncidences])

  const priorityFilterOptions = useMemo(() => ([
    { value: 'ALL', label: t_('All'), color: theme.palette.text.secondary },
    { value: 'LOW', label: t_('Low'), color: theme.palette.success.main },
    { value: 'MEDIUM', label: t_('Medium'), color: theme.palette.warning.main },
    { value: 'HIGH', label: t_('High'), color: theme.palette.warning.dark },
    { value: 'CRITICAL', label: t_('Critical'), color: theme.palette.error.main }
  ]), [t_, theme.palette])

  const priorityColorByValue = useMemo(() => {
    const map = new Map()
    priorityFilterOptions.forEach((item) => map.set(item.value, item.color))
    return map
  }, [priorityFilterOptions])

  const handleSelectOpenIncidenceUuid = useCallback((uuid) => {
    setSelectedOpenIncidenceUuid(uuid)
  }, [])

  const selectedOpenIncidenceStatusColor = useMemo(() => {
    const normalized = String(selectedOpenIncidenceStatus || '').trim().toUpperCase()
    if (normalized === 'CLOSED') return theme.palette.success.main
    if (normalized === 'PENDING' || normalized === 'PENDING_ACTION' || normalized === 'OPEN') return theme.palette.warning.main
    if (normalized === 'ANALYSIS') return theme.palette.info.main
    return theme.palette.text.secondary
  }, [selectedOpenIncidenceStatus, theme.palette])

  const selectedOpenIncidencePriorityColor = useMemo(() => {
    const normalized = String(selectedOpenIncidencePriority || '').trim().toUpperCase()
    const option = priorityFilterOptions.find((item) => item.value === normalized)
    return option?.color || theme.palette.text.secondary
  }, [priorityFilterOptions, selectedOpenIncidencePriority, theme.palette.text.secondary])

  const historyPanelsHeight = isSmallScreen ? '70vh' : '70vh'

  const listView = (
    <QualityIncidenceHistoryView
      t_={t_}
      theme={theme}
      isSmallScreen={isSmallScreen}
      historyPanel={historyPanel}
      setHistoryPanel={setHistoryPanel}
      detailsTab={detailsTab}
      setDetailsTab={setDetailsTab}
      historyPanelsHeight={historyPanelsHeight}
      onOpenCreateForm={openCreateForm}
      onOpenEditForm={openEditForm}
      canEditSelectedDraft={Boolean(selectedOpenIncidenceDisplay?.uuid) && selectedOpenIncidenceStatus === 'DRAFT'}
      onCloseDialog={closeDialog}
      filteredOpenIncidences={filteredOpenIncidences}
      selectedOpenIncidence={selectedOpenIncidence}
      isLoadingOpenIncidences={isLoadingOpenIncidences}
      isLoadingOpenIncidencesError={isLoadingOpenIncidencesError}
      onRefreshOpenIncidences={handleRefreshOpenIncidences}
      incidenceSearchQuery={incidenceSearchQuery}
      onIncidenceSearchQueryChange={(event) => setIncidenceSearchQuery(event.target.value)}
      onClearIncidenceSearchQuery={() => setIncidenceSearchQuery('')}
      priorityFilter={priorityFilter}
      priorityFilterOptions={priorityFilterOptions}
      onPriorityFilterChange={setPriorityFilter}
      onClearFilters={clearListFilters}
      priorityColorByValue={priorityColorByValue}
      onSelectOpenIncidenceUuid={handleSelectOpenIncidenceUuid}
      formatDate={formatDate}
      selectedOpenIncidenceCode={selectedOpenIncidenceCode}
      selectedOpenIncidenceLabel={selectedOpenIncidenceLabel}
      selectedOpenIncidenceStatus={selectedOpenIncidenceStatus}
      selectedOpenIncidenceStatusColor={selectedOpenIncidenceStatusColor}
      selectedOpenIncidencePriority={selectedOpenIncidencePriority}
      selectedOpenIncidencePriorityColor={selectedOpenIncidencePriorityColor}
      selectedOpenIncidenceCreatedDate={selectedOpenIncidenceCreatedDate}
      selectedOpenIncidenceEndDate={selectedOpenIncidenceEndDate}
      selectedOpenIncidenceFabricationOrderName={selectedOpenIncidenceFabricationOrderName}
      selectedOpenIncidenceJobName={selectedOpenIncidenceJobName}
      selectedOpenIncidenceDeviceName={selectedOpenIncidenceDeviceName}
      selectedOpenIncidenceWorkerName={selectedOpenIncidenceWorkerName}
      selectedOpenIncidenceResponsibleName={selectedOpenIncidenceResponsibleName}
      selectedOpenIncidenceDescription={selectedOpenIncidenceDescription}
      selectedOpenIncidencePreliminaryAnalysis={selectedOpenIncidencePreliminaryAnalysis}
      selectedOpenIncidenceProposedSolution={selectedOpenIncidenceProposedSolution}
      isLoadingSelectedOpenIncidenceDetails={isLoadingSelectedOpenIncidenceDetails}
      isLoadingSelectedOpenIncidenceDetailsError={isLoadingSelectedOpenIncidenceDetailsError}
      selectedOpenIncidenceScopeRows={selectedOpenIncidenceScopeRows}
      selectedOpenIncidenceActionRows={selectedOpenIncidenceActionRows}
      isLoadingSelectedOpenIncidenceActions={isLoadingSelectedOpenIncidenceActions}
      isLoadingSelectedOpenIncidenceActionsError={isLoadingSelectedOpenIncidenceActionsError}
      onActionExecuted={handleActionExecuted}
    />
  )

  const handleCodeChange = useCallback((nextCode) => {
    setIncidenceForm((prev) => ({ ...prev, code: nextCode }))
  }, [])

  const handleTitleChange = useCallback((nextTitle) => {
    setIncidenceForm((prev) => ({ ...prev, title: nextTitle }))
  }, [])

  const handleDescriptionChange = useCallback((nextDescription) => {
    setIncidenceForm((prev) => ({ ...prev, description: nextDescription }))
  }, [])

  const handleDescriptionInputChange = useCallback((event) => {
    handleDescriptionChange(event.target.value)
  }, [handleDescriptionChange])

  const handleStatusChange = useCallback((nextLabel) => {
    const nextStatus = statusValueByLabel.get(nextLabel) || 'draft'
    setIncidenceForm((prev) => ({ ...prev, status: nextStatus }))
  }, [statusValueByLabel])

  const handlePriorityChange = useCallback((nextLabel) => {
    const nextPriority = priorityValueByLabel.get(nextLabel) || 'LOW'
    setIncidenceForm((prev) => ({ ...prev, priority: nextPriority }))
  }, [priorityValueByLabel])

  const handleCreatedDateChange = useCallback((nextValue) => {
    setIncidenceForm((prev) => ({ ...prev, createdDate: nextValue || prev.createdDate }))
  }, [])

  const handlePreliminaryAnalysisChange = useCallback((preliminaryAnalysis) => {
    setIncidenceForm((prev) => ({ ...prev, preliminaryAnalysis }))
  }, [])

  const handlePreliminaryAnalysisInputChange = useCallback((event) => {
    handlePreliminaryAnalysisChange(event.target.value)
  }, [handlePreliminaryAnalysisChange])

  const handleProposedSolutionChange = useCallback((nextProposedSolution) => {
    setIncidenceForm((prev) => ({ ...prev, proposedSolution: nextProposedSolution }))
  }, [])

  const handleProposedSolutionInputChange = useCallback((event) => {
    handleProposedSolutionChange(event.target.value)
  }, [handleProposedSolutionChange])

  const handleJobChange = useCallback((nextLabel) => {
    if (manualContext) {
      const selectedJob = manualJobOptions.find((option) => option.name === nextLabel) || null
      setSelectedManualNode(selectedJob ? { ...selectedJob.raw, id: selectedJob.id, name: selectedJob.name } : null)
      setIncidenceForm((prev) => ({
        ...prev,
        jobLabel: nextLabel || '',
        scopeRows: []
      }))
      return
    }
    setIncidenceForm((prev) => ({ ...prev, jobLabel: nextLabel || '' }))
  }, [manualContext, manualJobOptions])

  const handleFabricationOrderChange = useCallback((nextLabel) => {
    const selectedOrder = manualFabricationOrders.find((option) => option.code === nextLabel) || null
    setSelectedManualFabricationOrder(selectedOrder ? { ...selectedOrder.raw, id: selectedOrder.id, code: selectedOrder.code, name: selectedOrder.code } : null)
    setSelectedManualNode(null)
    setManualJobOptions([])
    setIncidenceForm((prev) => ({
      ...prev,
      jobLabel: '',
      scopeRows: []
    }))
  }, [manualFabricationOrders])

  const handleDeviceChange = useCallback((nextLabel) => {
    const selectedDevice = manualDeviceOptions.find((option) => option.name === nextLabel) || null
    setSelectedManualDevice(selectedDevice ? { ...selectedDevice.raw, id: selectedDevice.id, name: selectedDevice.name } : null)
  }, [manualDeviceOptions])

  const handleWorkerChange = useCallback((nextLabel) => {
    const selectedWorker = dialogData.workerByLabel.get(nextLabel) || null
    setSelectedManualWorker(selectedWorker)
  }, [dialogData.workerByLabel])

  const handleScopeRowsChange = useCallback((nextRows) => {
    setIncidenceForm((prev) => ({ ...prev, scopeRows: nextRows }))
  }, [])

  const handleScopesEndDateChange = useCallback((nextValue) => {
    setIncidenceForm((prev) => ({ ...prev, scopesEndDate: nextValue }))
  }, [])

  const handleActionsChange = useCallback((nextActions) => {
    setIncidenceForm((prev) => ({ ...prev, incidenceActions: nextActions }))
  }, [])

  const handleResponsibleChange = useCallback((nextLabel) => {
    setIncidenceForm((prev) => ({ ...prev, responsibleName: nextLabel }))
  }, [])

  const isTitleValid = incidenceForm.title?.trim().length > 0
  const isDescriptionValid = incidenceForm.description?.trim().length > 0
  const isStatusValid = String(incidenceForm.status || '').trim().length > 0
  const isPriorityValid = String(incidenceForm.priority || '').trim().length > 0
  const isCreatedDateValid = incidenceForm.createdDate instanceof Date && !Number.isNaN(incidenceForm.createdDate.getTime())
  const isEndDateRequired = String(incidenceForm.status || '').trim().toUpperCase() === 'CLOSED'
  const isEndDateValid = !isEndDateRequired || (incidenceForm.endDate instanceof Date && !Number.isNaN(incidenceForm.endDate.getTime()))
  const isFabricationOrderValid = Boolean(parseLongId(creationFabricationOrder?.id))
  const isDeviceValid = manualContext || Boolean(parseLongId(creationDevice?.id))
  const isWorkerValid = manualContext || Boolean(worker)
  // Every scope must have at least one job — the backend stores one scope per (OF, job) pair,
  // a scope with no jobs would silently become an orphan with node=null.
  const isScopesValid = Array.isArray(incidenceForm.scopeRows) &&
    incidenceForm.scopeRows.length > 0 &&
    incidenceForm.scopeRows.every((row) => Array.isArray(row?.jobs) && row.jobs.length > 0)
  const hasSavedCode = String(incidenceForm.code || '').trim().length > 0
  const isFormValid = isTitleValid && isDescriptionValid && isStatusValid && isPriorityValid && isCreatedDateValid && isEndDateValid && isDeviceValid && isWorkerValid && isScopesValid && !hasSavedCode
  const isFormLocked = isSaving || hasSavedCode

  const findSavedQualityIncidenceUuid = useCallback(async (savedCode) => {
    const normalizedSavedCode = String(savedCode || '').trim()
    if (!normalizedSavedCode) return ''

    const fabricationOrderId = creationFabricationOrder?.id || 'null'
    const nodeUUID = creationNode?.uuid || creationNode?.id || ''
    if (!nodeUUID) return ''

    const response = await apiGet(`qualityIncidence/${fabricationOrderId}/${nodeUUID}`)
    const savedIncidence = normalizeApiList(response).find((incidence) => String(incidence?.code || '').trim() === normalizedSavedCode)
    return String(savedIncidence?.uuid || '').trim()
  }, [creationFabricationOrder?.id, creationNode?.uuid, creationNode?.id])

  const openDocumentsDialog = useCallback(() => {
    if (!currentUuid) {
      showAlert({
        severity: 'warning',
        title: t_('Save the quality incidence before adding documents')
      })
      return
    }
    setOpenDocument(true)
  }, [currentUuid, showAlert, t_])

  const checkIfIsNotEmpty = useCallback((value, fieldName) => {
    if (!value) {
      return `${t_(fieldName)} ${t_('Can not be empty').toLocaleLowerCase()}`
    }
    return ''
  }, [t_])

  const checkCodeError = useCallback(() => {
    return ''
  }, [])

  const descriptionErrorMessage = useMemo(() => {
    if (!isDescriptionValid) {
      return t_('Description') + ' ' + t_('Can not be empty').toLocaleLowerCase()
    }
    return ''
  }, [isDescriptionValid, t_])

  const onSubmitCreate = useCallback(async (event) => {
    event.preventDefault()
    setHasTriedSubmitIncidenceForm(true)
    if (!isFormValid || isSaving) {
      if (!isScopesValid && !isSaving) {
        const rows = Array.isArray(incidenceForm.scopeRows) ? incidenceForm.scopeRows : []
        const scopeMissingJob = rows.find((row) => !Array.isArray(row?.jobs) || row.jobs.length === 0)
        showAlert({
          severity: 'warning',
          title: scopeMissingJob
            ? `${t_('Scope')} ${scopeMissingJob.fabricationOrderCode || ''} ${t_('must have at least one job')}`.trim()
            : t_('At least one scope is required')
        })
      }
      return
    }
    const responsibleName = incidenceForm.responsibleName
    const qualityIncidenceDto = buildQualityIncidenceDtoFromForm({
      incidenceForm,
      fabricationOrder: creationFabricationOrder,
      node: creationNode,
      device: creationDevice,
      worker: creationWorker,
      responsibleName
    })
    try {
      setIsSaving(true)
      const response = await apiPost('qualityIncidence', qualityIncidenceDto)
      const savedCode = getQualityIncidenceCodeFromResponse(response)
      const savedUuid = getQualityIncidenceUuidFromResponse(response) || await findSavedQualityIncidenceUuid(savedCode)

      if (savedCode) {
        setIncidenceForm((prev) => ({ ...prev, code: savedCode }))
        if (savedUuid) setCurrentUuid(savedUuid)
        openIncidencesCacheRef.current = { ts: 0, incidences: null }
        handleRefreshSummary()
        onSaved?.(savedCode)
        showAlert({
          severity: 'success',
          title: t_('Quality incidence saved'),
          message: t_('Incidence code: ') + savedCode
        })
      }
    } catch (error) {
      console.error('qualityIncidence onSubmit error', error)
      showAlert({
        severity: 'error',
        title: t_('Error saving quality incidence'),
        message: error?.message || t_('Please check the form and try again')
      })
    } finally {
      setIsSaving(false)
    }
  }, [creationDevice, creationFabricationOrder, creationNode, creationWorker, findSavedQualityIncidenceUuid, handleRefreshSummary, incidenceForm, isFormValid, isSaving, isScopesValid, onSaved, showAlert, t_])

  const onSubmitEdit = async (data) => {
    setIsSaving(true)
    try {
      const editEndpoint = 'qualityIncidence'
      await apiPut(editEndpoint, data)
      openIncidencesCacheRef.current = { ts: 0, incidences: null }
      handleRefreshSelectedOpenIncidenceDetails(data?.uuid)
      handleRefreshSummary()
      handleRefreshOpenIncidences()
      showAlert({
        severity: 'success',
        title: t_('Quality incidence edited')
      })
      return true
    } catch (error) {
      console.error('qualityIncidence onSubmit error', error)
      if (!error?.message) {
        error.message = t_('Please check the form and try again')
      }
      throw error
    } finally {
      setIsSaving(false)
    }
  }
  const formToCreateIncidence = () => {
    return (
      <QualityIncidenceCreateForm
        t_={t_}
        theme={theme}
        format={format}
        onSubmit={onSubmitCreate}
        onClose={manualContext ? closeDialog : () => setIsCreateFormOpen(false)}
        isSaving={isSaving}
        isFormLocked={isFormLocked}
        showRequiredErrors={hasTriedSubmitIncidenceForm}
        incidenceForm={incidenceForm}
        statusSelectOptions={statusSelectOptions}
        statusLabelByValue={statusLabelByValue}
        prioritySelectOptions={prioritySelectOptions}
        priorityLabelByValue={priorityLabelByValue}
        isLoadingWorkers={dialogData.isLoadingWorkers}
        isLoadingWorkflowResources={dialogData.isLoadingWorkflowResources}
        fabricationOrderDisplayName={fabricationOrderDisplayName}
        fabricationOrderSelectOptions={isLoadingManualFabricationOrders ? [] : manualFabricationOrders.map((option) => option.code)}
        handleFabricationOrderChange={handleFabricationOrderChange}
        isFabricationOrderSelectable={manualContext}
        isFabricationOrderValid={isFabricationOrderValid}
        jobDisplayName={jobDisplayName}
        jobSelectOptions={manualJobOptions.map((option) => option.name)}
        isJobSelectable={manualContext}
        deviceDisplayName={deviceDisplayName}
        deviceSelectOptions={manualDeviceOptions.map((option) => option.name)}
        handleDeviceChange={handleDeviceChange}
        isDeviceSelectable={manualContext}
        isLoadingDevices={isLoadingManualDevices}
        workerDisplayName={workerDisplayName}
        workerSelectOptions={dialogData.workerOptions}
        handleWorkerChange={handleWorkerChange}
        isWorkerSelectable={manualContext}
        isDescriptionValid={isDescriptionValid}
        descriptionErrorMessage={descriptionErrorMessage}
        handleTitleChange={handleTitleChange}
        handleCodeChange={handleCodeChange}
        handleStatusChange={handleStatusChange}
        handlePriorityChange={handlePriorityChange}
        handleCreatedDateChange={handleCreatedDateChange}
        handleJobChange={handleJobChange}
        handleResponsibleChange={handleResponsibleChange}
        handleDescriptionInputChange={handleDescriptionInputChange}
        handlePreliminaryAnalysisInputChange={handlePreliminaryAnalysisInputChange}
        handleProposedSolutionInputChange={handleProposedSolutionInputChange}
        handleScopeRowsChange={handleScopeRowsChange}
        handleScopesEndDateChange={handleScopesEndDateChange}
        handleActionsChange={handleActionsChange}
        checkIfIsNotEmpty={checkIfIsNotEmpty}
        checkCodeError={checkCodeError}
        isStatusValid={isStatusValid}
        isPriorityValid={isPriorityValid}
        AlertElement={AlertElement}
        openDocumentsDialog={openDocumentsDialog}
        isDocumentsDisabled={!currentUuid || isSaving || manualContext}
      />
    )
  }

  return (
    <>
      {!hideTrigger && (
        <WorkerRequiredButton
          onClick={openDialog}
          disabled={!isJobActive}
          focusRipple
          aria-label={t_('Open quality incidence form')}
          aria-haspopup='dialog'
          component={ButtonBase}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'stretch',
            textAlign: 'left',
            opacity: isJobActive ? 1 : 0.6,
            cursor: isJobActive ? 'pointer' : 'default',
            transition: theme.transitions.create(['opacity', 'background-color', 'filter']),
            '&:hover': {
              backgroundColor: isJobActive ? alpha(theme.palette.common.white, 0.04) : 'transparent'
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2
            }
          }}
        >
          <Stack sx={{ height: '100%', width: '100%' }} gap={1.25}>
            {isLoadingSummary
              ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                  <Skeleton variant='text' sx={{ width: '60%', fontSize: '0.8rem' }} />
                  <Skeleton variant='rectangular' height={40} sx={{ borderRadius: 1 }} />
                  <Skeleton variant='text' sx={{ width: '40%', fontSize: '0.6rem' }} />
                </Box>
                )
              : (
                <Stack gap={0.25}>
                  <Typography
                    variant='overline'
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      lineHeight: 1.1,
                      letterSpacing: 1.2
                    }}
                  >
                    {t_('Last incident')}
                  </Typography>

                  {activeIncident
                    ? (
                      <Stack gap={0.5}>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {activeIncident.code ? `${activeIncident.code} · ${activeIncident.title || activeIncident.name}` : (activeIncident.title || activeIncident.name)}
                        </Typography>

                        <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 1,
                              backgroundColor: alpha(priorityColorByValue.get(String(activeIncident.priority).toUpperCase()) || theme.palette.text.secondary, 0.15),
                              border: `1px solid ${alpha(priorityColorByValue.get(String(activeIncident.priority).toUpperCase()) || theme.palette.text.secondary, 0.3)}`
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.65rem',
                                color: priorityColorByValue.get(String(activeIncident.priority).toUpperCase()) || 'text.secondary',
                                textTransform: 'uppercase'
                              }}
                            >
                              {priorityLabelByValue.get(String(activeIncident.priority).toUpperCase()) || activeIncident.priority}
                            </Typography>
                          </Box>

                          <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                            {statusLabelByValue.get(String(activeIncident.status).toLowerCase()) || activeIncident.status}
                          </Typography>

                          {activeIncident.createdDate && (
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.7rem', opacity: 0.8 }}>
                              {formatDate(activeIncident.createdDate)}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                      )
                    : (
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.25,
                          fontStyle: 'italic'
                        }}
                      >
                        {isJobActive ? t_('No active incident') : t_('No active job')}
                      </Typography>
                      )}
                </Stack>
                )}

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              {severityOptions.map((severityOption) => (
                <SeverityCard
                  key={severityOption.key}
                  label={severityOption.label}
                  value={Number(severityCounts[severityOption.key] || 0)}
                  color={severityOption.color}
                />
              ))}
            </Box>
          </Stack>
        </WorkerRequiredButton>
      )}
      <SmartDialog
        setOpen={isDialogOpen}
        closeCallback={closeDialog}
        closeOnBackdrop={!isSaving}
        closeOnEscape={!isSaving}
        cancel={false}
        accept={false}
        close={false}
        scroll='paper'
        minWidth={isSmallScreen ? '95vw' : isCreateFormOpen ? '70vw' : '78vw'}
        paperWidth={isSmallScreen ? '95vw' : isCreateFormOpen ? '70vw' : '78vw'}
        paperHeight={isSmallScreen ? '92vh' : isCreateFormOpen ? '70vh' : '76vh'}
        growContainerHeight
        renderComponent={
          manualContext ? formToCreateIncidence() : isCreateFormOpen ? formToCreateIncidence() : listView
          }
      />
      <SmartDialog
        title={{ icon: <EditIcon />, render: <Typography variant='h4'>{t_('Quality Incidence edit')}</Typography> }}
        setOpen={showForm}
        cancelCallback={() => setShowForm()}
        acceptCallback={async () => {
          const success = await formRef.current?.submit()
          if (success) {
            setShowForm()
          }
        }}
        renderComponent={
          <QualityIncidenceForm
            ref={formRef}
            qualityIncidenceUuid={currentUuid}
            allowDraftStatusOption
            onSubmit={(data) => onSubmitEdit(data)}
            entryMode='MOS'
          />
        }
      />
      <SmartDialog
        closeCallback={() => setOpenDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: currentUuid, code: incidenceForm.code }}
            resourceType='QUALITY_INCIDENCE'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={incidenceForm.title}
          />
        }
        setOpen={openDocument}
      />
    </>
  )
}

SeverityCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
}

QualityIncidenceWidget.propTypes = {
  createDialogOpen: PropTypes.bool,
  data: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    }),
    counts: PropTypes.shape({
      low: PropTypes.number,
      medium: PropTypes.number,
      high: PropTypes.number,
      critical: PropTypes.number
    })
  }),
  hideTrigger: PropTypes.bool,
  manualContext: PropTypes.bool,
  onCreateDialogClose: PropTypes.func,
  onSaved: PropTypes.func
}

class QualityIncidenceWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'QualityIncidenceWidgetProvider'

  getName = () => 'Quality Incidence'

  getIcon = () => ReportProblemOutlinedIcon

  getDefaultConfiguration = () => DEFAULT_WIDGET_DATA

  createWidget = (props) => <QualityIncidenceWidget {...props} />

  createPreviewWidget = (props) => <QualityIncidenceWidget {...props} data={PREVIEW_DATA} />

  size = { defaultW: 2, defaultH: 4, minW: 2, minH: 4 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export default QualityIncidenceWidgetProvider
